/**
 * BlockWrapper Component
 * 
 * Wraps all block components with selection, hover, and drag-drop functionality.
 * Provides consistent UI for block interactions in the builder canvas.
 */

import React, { memo, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { useMenuBuilderStore, useIsBlockSelected } from '@/stores/menuBuilderStore';
import { AnyMenuBlock, getBlockMetadata, BlockType } from '@/types/menuBuilder';
import { 
  GripVertical, 
  Trash2, 
  Copy,
  LayoutTemplate,
  Grid3x3,
  Star,
  Image,
  Type,
  Minus,
  Space,
  Search,
  List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BlockWrapperProps {
  block: AnyMenuBlock;
  children: React.ReactNode;
  isPreview?: boolean;
}

// Map block types to their Lucide icons
const BLOCK_ICONS: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  header: LayoutTemplate,
  category: Grid3x3,
  featured: Star,
  banner: Image,
  text: Type,
  divider: Minus,
  spacer: Space,
  search: Search,
  'category-navigation': List,
};

export const BlockWrapper = memo(function BlockWrapper({
  block,
  children,
  isPreview = false,
}: BlockWrapperProps) {
  const selectBlock = useMenuBuilderStore((s) => s.selectBlock);
  const removeBlock = useMenuBuilderStore((s) => s.removeBlock);
  const duplicateBlock = useMenuBuilderStore((s) => s.duplicateBlock);
  const isSelected = useIsBlockSelected(block.id);
  const hoveredBlockId = useMenuBuilderStore((s) => s.hoveredBlockId);
  const hoverBlock = useMenuBuilderStore((s) => s.hoverBlock);
  const isHovered = hoveredBlockId === block.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: isPreview,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return;
      e.stopPropagation();
      selectBlock(block.id);
    },
    [block.id, isPreview, selectBlock]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeBlock(block.id);
    },
    [block.id, removeBlock]
  );

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      duplicateBlock(block.id);
    },
    [block.id, duplicateBlock]
  );

  const handleMouseEnter = useCallback(() => {
    if (!isPreview) {
      hoverBlock(block.id);
    }
  }, [block.id, isPreview, hoverBlock]);

  const handleMouseLeave = useCallback(() => {
    if (!isPreview) {
      hoverBlock(null);
    }
  }, [isPreview, hoverBlock]);

  const metadata = getBlockMetadata(block.type);
  const Icon = BLOCK_ICONS[block.type];

  // Preview mode - no interactions
  if (isPreview) {
    return <div className="block-preview">{children}</div>;
  }

  if (!metadata) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'group relative rounded-lg transition-all duration-200',
        isDragging && 'opacity-30',
        isSelected && 'ring-2 ring-primary ring-offset-2',
        isHovered && !isSelected && 'ring-1 ring-muted-foreground/30',
        !isSelected && !isHovered && 'hover:ring-1 hover:ring-muted-foreground/20'
      )}
    >
      {/* Block toolbar - appears at top when selected or hovered */}
      <div
        className={cn(
          'absolute -top-9 left-0 right-0 z-20 flex items-center justify-between px-2 py-1 rounded-t-lg transition-opacity',
          'bg-muted/95 backdrop-blur-sm border border-b-0',
          isSelected && 'bg-primary/10',
          !isSelected && !isHovered && 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'
        )}
      >
        {/* Left side - block type and drag handle */}
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Drag to reorder</TooltipContent>
          </Tooltip>
          
          <div className={cn(
            'flex items-center gap-1 text-xs font-medium',
            isSelected ? 'text-primary' : 'text-muted-foreground'
          )}>
            <Icon className="h-3 w-3" />
            <span>{metadata.name}</span>
          </div>
        </div>

        {/* Right side - actions */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleDuplicate}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Duplicate</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-destructive hover:text-destructive-foreground"
                onClick={handleDelete}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Delete</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Block content */}
      <div className="relative">{children}</div>
    </div>
  );
});

export default BlockWrapper;
